import { useState } from 'react'
import { Box, Checkbox, CircularProgress, IconButton, Typography, alpha } from '@mui/material'
import SyncIcon from '@mui/icons-material/Sync'
import CloudSyncIcon from '@mui/icons-material/CloudSync'

import {
  MetaChip,
  ScorePills,
  StatusBadge,
  UPGRADE,
  UPGRADE_INK,
  runtimeTone
} from './boardBadges'
import { TreeNode } from './boardTree'
import { formatLastScannedLabel } from '../../utils/socrVulnBoard'

export const SourceLeaf = ({ source, dtVersion, color, mode = 'vulnerabilities' }) => {
  const isUpgrades = mode === 'upgrades'
  const branch = source.branch || dtVersion || ''
  const assessment = source.assessment || {}

  let bits = []
  if (isUpgrades) {
    bits = [
      source.runtime ? `runtime ${source.runtime}` : null,
      source.kind ? source.kind : null,
      branch ? `branch/version ${branch}` : null
    ].filter(Boolean)
  } else if (branch) {
    bits = [`version ${branch}`]
  }

  return (
    <Box
      sx={{
        px: 1.1,
        py: 0.75,
        borderRadius: 1,
        border: `1px dashed ${alpha(color, 0.3)}`,
        backgroundColor: alpha(color, 0.06)
      }}
    >
      {isUpgrades
        ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mb: 0.35 }}>
            {assessment.status === 'behind'
              ? (
                <MetaChip
                  dense
                  label={`Upgrade → ${assessment.target || 'current'}`}
                  tone={UPGRADE.behind}
                />
                )
              : null}
            {assessment.status === 'unknown'
              ? <MetaChip dense label='Unknown' tone={UPGRADE.unknown} />
              : null}
            {assessment.status === 'ok'
              ? <MetaChip dense label='Runtime OK' tone={UPGRADE.ok} />
              : null}
          </Box>
          )
        : null}
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '0.74rem',
          color: alpha('#fff', 0.88),
          wordBreak: 'break-all',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'
        }}
      >
        {source.sourcePath || '— path unavailable —'}
      </Typography>
      {bits.length
        ? (
          <Typography variant='caption' sx={{ color: alpha('#fff', 0.5), display: 'block', mt: 0.35 }}>
            {bits.join(' · ')}
          </Typography>
          )
        : null}
      {isUpgrades && assessment.reason
        ? (
          <Typography variant='caption' sx={{ color: alpha('#fff', 0.42), display: 'block', mt: 0.25 }}>
            {assessment.reason}
          </Typography>
          )
        : null}
    </Box>
  )
}

export const DtProjectNode = ({ project, color, mode = 'vulnerabilities' }) => {
  const [open, setOpen] = useState(false)
  const missing = Boolean(project?.missingInDt)
  const sources = Array.isArray(project?.sources) ? project.sources : []
  const hasDetail = sources.length > 0 || Boolean(project?.version)
  const isUpgrades = mode === 'upgrades'
  const behindCount = sources.filter((s) => s?.assessment?.status === 'behind').length
  const unknownCount = sources.filter((s) => {
    const status = s?.assessment?.status
    return !status || status === 'unknown'
  }).length
  const okCount = sources.filter((s) => s?.assessment?.status === 'ok').length
  const runtimeVersions = [...new Set(
    sources
      .map((s) => s?.assessment?.runtime || s?.runtime || '')
      .map((v) => String(v || '').trim())
      .filter(Boolean)
  )]

  let subtitle
  if (isUpgrades) {
    subtitle = [
      project.version ? `DT ${project.version}` : null,
      runtimeVersions.length ? runtimeVersions.join(' · ') : null,
      behindCount
        ? `${behindCount} behind`
        : unknownCount
          ? `${unknownCount} unknown`
          : (okCount ? 'ok' : null)
    ].filter(Boolean).join(' · ')
  } else {
    subtitle = [
      project.version ? `DT ${project.version}` : null,
      `${(Number(project.totalFindings) || 0).toLocaleString()} findings`,
      `risk ${Number(project.inheritedRiskScore || 0).toLocaleString()}`
    ].filter(Boolean).join(' · ')
  }

  let trailing = null
  if (missing) trailing = <MetaChip dense label='Not in DT' tone={UPGRADE.missing} />
  else if (!isUpgrades) trailing = <ScorePills severityCounts={project.severityCounts} dense includeLow />
  else if (behindCount) {
    trailing = (
      <StatusBadge
        dense
        label={`${behindCount} ↑`}
        color={UPGRADE.behind}
        ink={UPGRADE_INK.behind}
      />
    )
  } else if (unknownCount || sources.length === 0) {
    trailing = <MetaChip dense label='Unknown' tone={UPGRADE.unknown} />
  } else {
    trailing = <MetaChip dense label='Runtimes OK' tone={UPGRADE.ok} />
  }

  return (
    <TreeNode
      color={color}
      depth={2}
      title={project.projectName || '—'}
      subtitle={subtitle}
      open={open}
      onToggle={() => setOpen((v) => !v)}
      expandable={hasDetail || missing}
      trailing={trailing}
    >
      {(sources.length
        ? sources
        : [{ sourcePath: '', runtime: '', kind: '', branch: '' }]
      ).map((source, idx) => (
        <SourceLeaf
          key={`${project.projectName}-${source.sourcePath || idx}`}
          source={source}
          dtVersion={project.version}
          color={color}
          mode={mode}
        />
      ))}
    </TreeNode>
  )
}

export const RepoNode = ({
  repo,
  color,
  mode = 'vulnerabilities',
  onRefreshRepoDt = null,
  refreshingRepoDt = false,
  refreshDisabled = false,
  onRefreshRepoBitbucket = null,
  refreshingRepoBitbucket = false,
  refreshBitbucketDisabled = false
}) => {
  const [open, setOpen] = useState(false)
  const projects = repo.projects || []
  const isUpgrades = mode === 'upgrades'
  const projectRuntimes = Array.isArray(repo.projectRuntimes) ? repo.projectRuntimes : []
  const runtimeBehind = projectRuntimes.filter((rt) => rt?.assessment?.status === 'behind').length
  const runtimeUnknown = projectRuntimes.filter((rt) => {
    const status = rt?.assessment?.status
    return !status || status === 'unknown'
  }).length
  const runtimeOk = projectRuntimes.filter((rt) => rt?.assessment?.status === 'ok').length
  const runtimeVersions = [...new Set(
    projectRuntimes
      .map((rt) => rt?.assessment?.runtime || rt?.runtime || '')
      .map((v) => String(v || '').trim())
      .filter(Boolean)
  )]
  const runtimeVersionLabel = runtimeVersions.length > 4
    ? `${runtimeVersions.slice(0, 4).join(' · ')} · +${runtimeVersions.length - 4} more`
    : runtimeVersions.join(' · ')

  const runtimeLabel = runtimeVersionLabel ||
    `${projectRuntimes.length} runtime${projectRuntimes.length === 1 ? '' : 's'}`
  const projectLabel = `${projects.length} DT project${projects.length === 1 ? '' : 's'}`
  const uuid = String(repo.repositoryUuid || '').replace(/[{}]/g, '')

  let statusTrailing
  if (isUpgrades) {
    if (runtimeBehind) {
      statusTrailing = (
        <StatusBadge
          dense
          label={`${runtimeBehind} ↑`}
          color={UPGRADE.behind}
          ink={UPGRADE_INK.behind}
        />
      )
    } else if (runtimeUnknown || projectRuntimes.length === 0) {
      statusTrailing = <MetaChip dense label='Unknown' tone={UPGRADE.unknown} />
    } else if (runtimeOk) {
      statusTrailing = <MetaChip dense label='Runtimes OK' tone={UPGRADE.ok} />
    } else {
      statusTrailing = <MetaChip dense label='Unknown' tone={UPGRADE.unknown} />
    }
  } else {
    statusTrailing = <ScorePills severityCounts={repo.severityCounts} dense />
  }

  const syncDtControl = onRefreshRepoDt && uuid
    ? (
      <IconButton
        size='small'
        disabled={refreshDisabled || refreshingRepoDt}
        title='Sync DT project names for this repository'
        onClick={(e) => {
          e.stopPropagation()
          onRefreshRepoDt(uuid)
        }}
        sx={{
          color: alpha('#fff', 0.75),
          '&:hover': { color: '#fff', backgroundColor: alpha('#fff', 0.08) }
        }}
      >
        {refreshingRepoDt
          ? <CircularProgress size={16} color='inherit' />
          : <SyncIcon sx={{ fontSize: 18 }} />}
      </IconButton>
      )
    : null

  const syncBitbucketControl = onRefreshRepoBitbucket && uuid
    ? (
      <IconButton
        size='small'
        disabled={refreshBitbucketDisabled || refreshingRepoBitbucket}
        title='Start server-side .NET TFM rescan (.csproj). Client aborts the wait quickly; scan continues on the API.'
        onClick={(e) => {
          e.stopPropagation()
          onRefreshRepoBitbucket(uuid)
        }}
        sx={{
          color: alpha('#fff', 0.75),
          '&:hover': { color: '#fff', backgroundColor: alpha('#fff', 0.08) }
        }}
      >
        {refreshingRepoBitbucket
          ? <CircularProgress size={16} color='inherit' />
          : <CloudSyncIcon sx={{ fontSize: 18 }} />}
      </IconButton>
      )
    : null

  return (
    <TreeNode
      color={color}
      depth={1}
      title={repo.repositoryName || repo.repositorySlug || 'Repository'}
      subtitle={[
        repo.repositorySlug && repo.repositorySlug !== repo.repositoryName ? repo.repositorySlug : null,
        isUpgrades ? runtimeLabel : projectLabel
      ].filter(Boolean).join(' · ')}
      open={open}
      onToggle={() => setOpen((v) => !v)}
      trailing={(
        <>
          {isUpgrades ? syncBitbucketControl : syncDtControl}
          {statusTrailing}
        </>
      )}
    >
      {projects.length === 0
        ? (
          <Typography variant='body2' sx={{ color: alpha('#fff', 0.5), py: 0.5 }}>
            No Dependency-Track projects linked on this repository.
          </Typography>
          )
        : projects.map((project) => (
          <DtProjectNode
            key={`${repo.repositoryUuid}-${project.projectName}`}
            project={project}
            color={color}
            mode={mode}
          />
        ))}
    </TreeNode>
  )
}

export const ClientBanner = ({
  client,
  selected = false,
  onToggleSelect,
  mode = 'vulnerabilities',
  onRefreshRepoDt = null,
  refreshingRepoUuid = null,
  refreshDtDisabled = false,
  onRefreshRepoBitbucket = null,
  refreshingBitbucketRepoUuid = null,
  refreshBitbucketDisabled = false
}) => {
  const [open, setOpen] = useState(false)
  const color = client.color || '#6b7280'
  const runtimes = Array.isArray(client.runtimes) ? client.runtimes : []
  const repos = client.repositories || []
  const hasDt = Boolean(client.hasDt)
  const needsUpgrade = Boolean(client.needsUpgrade)
  const isUpgrades = mode === 'upgrades'
  const scannedLabel = formatLastScannedLabel(client.lastBomImport)

  const vulnSubtitle = [
    `${client.repoCount} repo${client.repoCount === 1 ? '' : 's'}`,
    `${client.dtProjectCount} DT project${client.dtProjectCount === 1 ? '' : 's'}`,
    `risk ${Number(client.inheritedRiskScore || 0).toLocaleString()}`,
    scannedLabel ? null : 'No scan date'
  ].filter(Boolean).join(' · ')

  const upgradeSubtitle = [
    `${client.repoCount} repo${client.repoCount === 1 ? '' : 's'}`,
    `${runtimes.length} runtime${runtimes.length === 1 ? '' : 's'}`,
    needsUpgrade ? 'needs upgrade' : null,
    scannedLabel || null
  ].filter(Boolean).join(' · ')

  return (
    <TreeNode
      color={color}
      depth={0}
      title={client.label}
      subtitle={isUpgrades ? upgradeSubtitle : vulnSubtitle}
      open={open}
      onToggle={() => setOpen((v) => !v)}
      leading={!isUpgrades
        ? (
          <Checkbox
            size='small'
            checked={selected}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation()
              onToggleSelect?.(e.target.checked)
            }}
            inputProps={{ 'aria-label': `Include ${client.label} in selection` }}
            sx={{
              p: 0.35,
              color: alpha('#fff', 0.55),
              '&.Mui-checked': { color: '#fff' }
            }}
          />
          )
        : null}
      trailing={(
        <>
          {!isUpgrades && scannedLabel
            ? <MetaChip dense label={scannedLabel} tone='#1e88e5' />
            : null}
          <MetaChip
            dense={isUpgrades}
            label={hasDt ? 'Has DT' : 'No DT'}
            tone={hasDt ? UPGRADE.ok : UPGRADE.missing}
          />
          {isUpgrades && needsUpgrade
            ? <MetaChip dense label='Needs upgrade' tone={UPGRADE.behind} />
            : null}
          {isUpgrades
            ? runtimes.map((rt) => {
              const label = typeof rt === 'string' ? rt : (rt.runtime || '—')
              const status = typeof rt === 'string' ? 'unknown' : rt.status
              const chipLabel = status === 'behind' ? `${label} ↑` : label
              return (
                <MetaChip
                  dense
                  key={`${label}-${status}`}
                  label={chipLabel}
                  tone={runtimeTone(status)}
                />
              )
            })
            : null}
          {!isUpgrades
            ? <ScorePills severityCounts={client.severityCounts} />
            : null}
        </>
      )}
    >
      {repos.length === 0
        ? (
          <Typography variant='body2' sx={{ color: alpha('#fff', 0.55), py: 0.75 }}>
            No repositories in the SOCR code tree for this client yet.
          </Typography>
          )
        : repos.map((repo) => {
          const repoUuid = String(repo.repositoryUuid || '').replace(/[{}]/g, '')
          return (
            <RepoNode
              key={repo.repositoryUuid || repo.repositorySlug || repo.repositoryName}
              repo={repo}
              color={color}
              mode={mode}
              onRefreshRepoDt={onRefreshRepoDt}
              refreshingRepoDt={Boolean(refreshingRepoUuid && refreshingRepoUuid === repoUuid)}
              refreshDisabled={refreshDtDisabled}
              onRefreshRepoBitbucket={onRefreshRepoBitbucket}
              refreshingRepoBitbucket={Boolean(
                refreshingBitbucketRepoUuid && refreshingBitbucketRepoUuid === repoUuid
              )}
              refreshBitbucketDisabled={refreshBitbucketDisabled}
            />
          )
        })}
    </TreeNode>
  )
}
